import { Fragment } from 'react'

import type { ShareAnnouncedPayload } from '~/api/types'
import { money } from '~/calendar/ListView'

import { Frame, Wordmark, link } from './Frame'
import { Line, Stop, meta } from './Line'
import { shortMonth, shortNight } from './night'
import s from './Share.module.css'

export type AnnouncedProps = Omit<ShareAnnouncedPayload, 'kind'>

type Gap = ShareAnnouncedPayload['between'][number]

/**
 * Format J: just announced, as a story.
 *
 * The picks the calendar first saw in the window, as acid stops on a grey line of the picks
 * that were already there. The grey rings are gaps, not gigs: one ring for every stretch of
 * old picks between two new ones, so the line is the calendar's shape and not its length,
 * which would be hundreds of rings. The ends say which month the line comes from and goes
 * on to. No time on a stop — months out the night is the news — and no pictures: the pill
 * is the calendar, and a link sticker goes over it by hand.
 *
 * Nothing here is anybody's: no faces, no marks.
 */
export function Announced({ host, frame, since, count, rows, between }: AnnouncedProps) {
  const drawn = rows.length
  const when = `since ${shortNight(since)}`
  const kick =
    drawn === count
      ? `${count} new ${count === 1 ? 'stop' : 'stops'} ${when}`
      : `${drawn} of ${count} new stops ${when}`

  // A gap draws only when it holds something; the first and the last name their month,
  // by the gap's earliest night, so the line reads "from September" and "on to November".
  const ring = (gap: Gap | undefined, at: number) => {
    if (!gap || gap.count === 0 || !gap.first) return null
    const month = shortMonth(gap.first)
    const label = at === 0 ? `${month} · already on the line` : at === drawn ? `${month} →` : ''
    return (
      <Stop stop="old">
        <span className={`${s.none} ${s.mono}`}>{label}</span>
      </Stop>
    )
  }

  return (
    <Frame format="J" frame={frame} className={s.announced}>
      <div className={s.head}>
        <div>
          <span className={`${s.kick} ${s.mono}`} data-probe="data">
            {kick}
          </span>
          <div className={`${s.big} ${s.disp}`} data-probe="display">
            Just announced
          </div>
        </div>
        <Wordmark />
      </div>
      <Line hue="grey">
        {rows.map((row, i) => (
          <Fragment key={row.id}>
            {ring(between[i], i)}
            <Stop stop="new">
              <div className={s.title}>{row.title}</div>
              <span className={`${s.meta} ${s.mono}`}>
                {meta(row, money(row), { night: true, time: false })}
              </span>
            </Stop>
          </Fragment>
        ))}
        {ring(between[drawn], drawn)}
      </Line>
      <div className={s.foot}>
        <span className={`${s.link} ${s.mono}`}>{link(host, '/calendar')}</span>
      </div>
    </Frame>
  )
}
